import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { of, forkJoin, merge, Observable, EMPTY } from 'rxjs';
import { 
  catchError, 
  map, 
  switchMap, 
  tap, 
  delay, 
  first,
  take} from 'rxjs/operators';

import * as DossierActions from './dossier.actions';
import * as DossierSelectors from './dossier.selectors';
import * as RecordActions from '../../record/store/record.actions';
import * as PanelActions from 'app/modules/shared-area/ducks/panel/store/panel.actions';

import { ErfassungInfoService } from 'app/core/services/erfassung-info/erfassung-info.service';
import { DossierHelperService } from 'app/modules/dossier-area/services/dossier-helper/dossier-helper.service';
import { DossierService } from 'app/modules/dossier-area/services/dossier/dossier.service';
import { ToastService } from 'app/core/services/toast-service';
import { I18nService } from 'app/core/services/i18n/i18n.service';
import { ConfirmDeleteResult, CustomWindowService } from 'app/core/services/custom-window-service/custom-window.service';
import { ViewSettingsDialogComponent } from '../dossier-edit/view-settings-dialog/view-settings-dialog.component';
import { getFachlektorMerkmale } from 'app/modules/shared-area/ducks/user-profile/store/user-profile.selectors';
import { Role } from 'app/modules/shared-area/ducks/user/models/role';
import { IAppState } from 'app/modules/shared-area/root-state';
import { ForbiddenResultReason, ViewMode } from 'app/core/services/api-client';
import { noOp } from 'app/core/utils/noOp';
import { ErfassungHelper } from 'app/modules/erfassungen/utils/erfassung.helper';

@Injectable()
export class DossierEffects {
  roleContext$: Observable<Role>;
  fachlektorMerkmale$: Observable<Set<number>>;

  constructor(
    private actions$: Actions,
    private store: Store<IAppState>,
    private dossierService: DossierService,
    private dossierHelperService: DossierHelperService,
    private router: Router,
    private toastyService: ToastService,
    private erfassungInfoService: ErfassungInfoService,
    private i18n: I18nService,
    private windowService: CustomWindowService,
  ) {
    this.roleContext$ = this.store.pipe(select(DossierSelectors.getRoleContext), first());
    this.fachlektorMerkmale$ = this.store.pipe(select(getFachlektorMerkmale), first());
  }

  // Revert all changes effects
  revertAllChanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.revertAllChanges),
      map(action => action.revertAllChanges),
      switchMap(revertAllChanges =>
        this.dossierService.revertAllChanges(revertAllChanges.id).pipe(
          map(() => {
            if (revertAllChanges.refresh) {
              return DossierActions.loadSubstance({ 
                loadSubstance: { id: revertAllChanges.id, viewMode: ViewMode.Original } 
              });
            } else {
              return DossierActions.revertAllChangesSuccess({ revertAllChanges });
            }
          }),
          catchError(error => of(DossierActions.revertAllChangesError())),
        ),
      ),
    )
  );

  revertAllChangesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.revertAllChangesSuccess),
      tap((action) => {
        this.toastyService.toastSuccessFormatted(
          'components.dossier.revertAllChangesSuccessTitle',
          'components.dossier.revertAllChangesSuccessMessage',
          {},
        );
        if (action.revertAllChanges.refresh !== true) {
          this.router.navigate(['/home']);
        }
      }),
    ), { dispatch: false }
  );

  // Stoff Freigabeprozess effects
  stoffFreigabeprozessStarten$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.stoffFreigabeprozessStartenEffect),
      switchMap(({erfassungId, message, bearbeiterUid, bearbeiter}) =>
        merge(
          of(DossierActions.submitSubstance()),
          this.dossierService.FreigabeProzessStarten(erfassungId, message, bearbeiterUid, bearbeiter).pipe(
            map(() => DossierActions.stoffFreigabeprozessStartenSuccessEffect()),
            catchError(error => of(DossierActions.stoffFreigabeprozessStartenErrorEffect({ error }))),
          ),
        ),
      ),
    )
  );

  stoffFreigabeprozessStartenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.stoffFreigabeprozessStartenSuccessEffect),
      tap(() => {
        this.toastyService.toastSuccessFormatted(
          'components.dossier.startApprovalProcessSuccessTitle',
          'components.dossier.startApprovalProcessSuccessMessage',
          {},
        );
        this.router.navigate(['/home']);
      }),
      map(() => DossierActions.submitSubstanceSuccess()),
    )
  );

  stoffFreigabeprozessStartenError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.stoffFreigabeprozessStartenErrorEffect),
      map(() => DossierActions.submitSubstanceError()),
    )
  );

  // Dossier löschen beantragen effects
  dossierLoeschenBeantragen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.dossierLoeschenBeantragen),
      switchMap(({id, message, bearbeiterUid, bearbeiter}) =>
        this.dossierService.StoffLoeschenBeantragen(id, message, bearbeiterUid, bearbeiter).pipe(
          map(() => DossierActions.dossierLoeschenBeantragenSuccess()),
          catchError(error => of(DossierActions.dossierLoeschenBeantragenError({ error }))),
        ),
      ),
    )
  );

  dossierLoeschenBeantragenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.dossierLoeschenBeantragenSuccess),
      tap(() => {
        this.toastyService.toastSuccessFormatted(
          'components.dossier.startApprovalProcessSuccessTitle',
          'components.dossier.startApprovalProcessSuccessMessage',
          {},
        );
        this.router.navigate(['/home']);
      }),
    ), { dispatch: false }
  );

  dossierLoeschenBeantragenError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.dossierLoeschenBeantragenError),
      tap((action) => {
        if (
          action.error &&
          (action.error as any).reason &&
          (action.error as any).reason === ForbiddenResultReason.ReferenceViolation
        ) {
          this.store.dispatch(RecordActions.referenceViolationOccured());
        }
      }),
    ), { dispatch: false }
  );

  // Load substance effect
  loadSubstance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.loadSubstance),
      map(action => action.loadSubstance),
      switchMap(action =>
        forkJoin([
          this.dossierService.loadSubstanceDossier(action.id, action.viewMode),
          action.searchId && /^[0-9A-Fa-f\-]{36}$/.test(action.searchId)
            ? this.dossierService.getTrefferListe(action.id, action.searchId)
            : of([]),
        ]).pipe(
          map(([dossier, trefferliste]) => {
            this.dossierHelperService.appendMatches(dossier, trefferliste);
            return DossierActions.loadSubstanceSuccess({ dossier });
          }),
          catchError(error => of(DossierActions.loadSubstanceError())),
        ),
      ),
    )
  );

  // Load erfassung effect
  loadErfassung$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.loadErfassung),
      map(action => action.erfassungRef),
      switchMap(payload =>
        forkJoin([
          this.dossierService.loadErfassung(payload.erfassungId),
          this.roleContext$,
          this.fachlektorMerkmale$,
        ]).pipe(
          map(([dossier, roleContext, fachlektorMerkmale]) => {
            this.dossierHelperService.appendRoleContext(dossier, roleContext, fachlektorMerkmale);
            return DossierActions.loadErfassungSuccess({ dossier });
          }),
          catchError(error => of(DossierActions.loadErfassungError())),
        ),
      ),
    )
  );

  // Delete erfassung effect
  deleteErfassung$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.deleteErfassung),
      map(action => action.erfassungRef),
      switchMap(erfassungRef => {
        return this.dossierService.deleteErfassung(erfassungRef.erfassungId).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.erfassungen.deleteSuccessTitle',
              'components.erfassungen.deleteSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.deleteErfassungError())),
        );
      }),
    ), { dispatch: false }
  );

  // Confirm as admin effect
  confirmAsAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.confirmAsAdminEffect),
      map(action => action.erfassungRef),
      switchMap(erfassungRef =>
        this.dossierService.confirmAsAdmin(erfassungRef.erfassungId).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.confirmSuccessTitle',
              'components.dossier.confirmSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.confirmAsAdminError())),
        ),
      ),
    ), { dispatch: false }
  );

  // Reject as admin effect
  rejectAsAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.rejectAsAdminEffect),
      map(action => action.erfassungRefWithReason),
      switchMap(erfassungRefWithReason =>
        this.dossierService.rejectAsAdmin(erfassungRefWithReason.erfassungId, erfassungRefWithReason.reason).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.rejectSuccessTitle',
              'components.dossier.rejectSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.rejectAsAdminError())),
        ),
      ),
    ), { dispatch: false }
  );

  // Confirm as lecturer effect
  confirmAsLecturer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.confirmAsLecturerEffect),
      map(action => action.erfassungRef),
      switchMap(erfassungRef =>
        this.dossierService.confirmAsLecturer(erfassungRef.erfassungId).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.confirmSuccessTitle',
              'components.dossier.confirmSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.confirmAsLecturerError())),
        ),
      ),
    ), { dispatch: false }
  );

  // Reject as lecturer effect
  rejectAsLecturer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.rejectAsLecturerEffect),
      map(action => action.erfassungRefWithReason),
      switchMap(erfassungRefWithReason =>
        this.dossierService.rejectAsLecturer(erfassungRefWithReason.erfassungId, erfassungRefWithReason.reason).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.rejectSuccessTitle',
              'components.dossier.rejectSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.rejectAsLecturerError())),
        ),
      ),
    ), { dispatch: false }
  );

  // Confirm merge effect
  confirmMerge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.confirmMergeEffect),
      map(action => action.erfassungRef),
      switchMap(erfassungRef =>
        this.dossierService.confirmMerge(erfassungRef.erfassungId).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.confirmSuccessTitle',
              'components.dossier.confirmSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.confirmMergeError())),
        ),
      ),
    ), { dispatch: false }
  );

  // Reject merge effect
  rejectMerge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.rejectMergeEffect),
      map(action => action.erfassungRefWithReason),
      switchMap(erfassungRefWithReason =>
        this.dossierService.rejectMerge(erfassungRefWithReason.erfassungId, erfassungRefWithReason.reason).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.rejectSuccessTitle',
              'components.dossier.rejectSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          catchError(error => of(DossierActions.rejectMergeError())),
        ),
      ),
    ), { dispatch: false }
  );

  // Re-edit dossier effect
  reEditDossierEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.reEditDossierEffect),
      map(action => action.reEdit),
      switchMap(reEdit =>
        this.dossierService.weiterarbeiten(reEdit.erfassungId).pipe(
          map(() => DossierActions.reEditDossierSuccess({ reEdit })),
          catchError(error => of(DossierActions.reEditDossierError())),
        ),
      ),
    )
  );

  reEditDossierSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.reEditDossierSuccess),
      tap((action) => {
        this.router.navigate(['/dossier/substance/', action.reEdit.id]);
      }),
    ), { dispatch: false }
  );

  // Withdraw erfassung effect
  withdrawErfassung$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.withdrawErfassung),
      map(action => action.erfassungRef),
      switchMap(payload =>
        this.dossierService.zurueckziehen(payload.erfassungId).pipe(
          tap(() => {
            this.toastyService.toastSuccessFormatted(
              'components.dossier.withdrawSuccessTitle',
              'components.dossier.withdrawSuccessMessage',
              {},
            );
            this.router.navigate(['/home']);
          }),
          map(() => noOp())
        ),
      ),
    ), { dispatch: false }
  );

  // Take erfassung effects
  takeErfassung$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.takeErfassung),
      map(action => action.takeErfassung),
      switchMap(payload =>
        this.dossierService.uebernehmen(payload.erfassungId).pipe(
          map(result => DossierActions.takeErfassungSuccess({ result })),
          catchError(error => of(DossierActions.takeErfassungError())),
        ),
      ),
    )
  );

  takeErfassungSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.takeErfassungSuccess),
      tap((action) => {
        const urlComponents = ErfassungHelper.openUrlByUebernehmenResult(action.result);
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigate(urlComponents));
      }),
    ), { dispatch: false }
  );

  // Start search effect
  startSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.startSearchEffect),
      switchMap(action =>
        merge(
          // emit this first: start search
          of(DossierActions.setSearchStateState({ searchState: 'searching' })),
          // do the search (use delay to let angular change detection work and show the spinner)
          of(action).pipe(
            delay(0),
            map(payload => ({
              payload,
              searchHits: this.dossierHelperService.getDossierSearchHits(
                payload.search,
                payload.isWholeWord,
                payload.isIgnoreCase,
              ),
            })),
            switchMap(({ payload, searchHits }) => [
              // open all panels which contains searchHits
              ...this.dossierHelperService.createOpenPanelsFromHitsActions(searchHits),
              // set search results in store
              DossierActions.setSearchState({
                search: payload.search,
                hits: searchHits,
                isIgnoreCase: payload.isIgnoreCase,
                isWholeWord: payload.isWholeWord,
                state: 'searched',
              }),
            ]),
          ),
        ),
      ),
    )
  );

  // Handle sachverhalt selection with validation
  sachverhaltSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.sachverhaltSelected),
      switchMap(action => 
        this.store.pipe(
          select(DossierSelectors.getSachverhaltToggle),
          first(),
          map(toggle => ({ action, toggle }))
        )
      ),
      switchMap(({ action, toggle }) => {
        const { sachverhaltId, erfassungSachverhaltId, merkmalName, merkmalId } = action;
        
        // Check if already selected
        const isAlreadySelected = toggle.selectedSachverhalte.some(
          s => s.sachverhaltId === sachverhaltId && s.erfassungSachverhaltId === erfassungSachverhaltId
        );

        if (isAlreadySelected) {
          // Remove from selection
          return of(DossierActions.removeSachverhaltFromSelection({ 
            sachverhaltId, 
            erfassungSachverhaltId 
          }));
        } else {
          // Validate merkmalId for feedback mode
          if (toggle.mode === 'feedback' && toggle.selectedSachverhalte.length > 0) {
            const firstSelectedMerkmalId = toggle.selectedSachverhalte[0].merkmalId;
            if (merkmalId !== firstSelectedMerkmalId) {
              // Show toast and don't add to selection
              this.toastyService.info({
                title: this.i18n.getLocalizedString('feedback.messages.multiAttribute.title'),
                msg: this.i18n.getLocalizedString('feedback.messages.multiAttribute.message'),
              });
              return EMPTY; // Return EMPTY to prevent any action from being dispatched
            }
          }
          
          // Add to selection
          return of(DossierActions.addSachverhaltToSelection({ 
            sachverhaltId, 
            erfassungSachverhaltId, 
            merkmalName, 
            merkmalId 
          }));
        }
      })
    )
  );

  openViewSettingsDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.openViewSettingsDialog),
      tap(() => {
        this.windowService.open({
          title: this.i18n.getLocalizedString('dialog.viewSettings.title'),
          content: ViewSettingsDialogComponent,
        });
      })
    ),
    { dispatch: false }
  );

  openDeleteViewConfirmDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.openDeleteViewConfirmDialog),
      tap(({ viewName }) => {
        this.windowService.confirmDelete(viewName)
          .pipe(take(1))
          .subscribe(result => {
            if (result === ConfirmDeleteResult.Yes) {
            this.store.dispatch(DossierActions.confirmDeleteView({ viewName }));
          } else {
            this.store.dispatch(DossierActions.cancelDeleteView());
          }
        });
      })
    ),
    { dispatch: false }
  );

  confirmDeleteView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.confirmDeleteView),
      map(({ viewName }) => PanelActions.deleteNamedView({ viewName }))
    )
  );

  // Load Assignable Users Effect
  loadAssignableUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DossierActions.loadAssignableUsers),
      switchMap(({ erfassungId }) =>
        this.erfassungInfoService.getAssignableUsers([erfassungId]).pipe(
          map(userItems => {
            return DossierActions.loadAssignableUsersSuccess({ users: userItems });
          }),
          catchError(error => {
            console.error('Error loading assignable users:', error);
            return of(DossierActions.loadAssignableUsersError({ 
              error: this.i18n.getLocalizedString('error.loadAssignableUsers') 
            }));
          })
        )
      )
    )
  );

}
